<template>
  <b-container fluid>
    <b-card no-body>
      <b-tabs align="center" content-class="mt-3">
        <b-tab title="Analytics" active>
          <b-card
            header="Settlement Analytics"
            header-text-variant="white"
            header-tag="header"
            header-bg-variant="warning"
          >
            <b-card>
              <b-row class="my-1">
                <b-col sm="3">
                  <b-form-datepicker
                    id="start-date-picker"
                    v-model="startDate"
                    @input="validateDates"
                  ></b-form-datepicker>
                </b-col>
                <b-col sm="3">
                  <b-form-datepicker
                    id="end-date-picker"
                    v-model="endDate"
                    @input="validateDates"
                  ></b-form-datepicker>
                </b-col>
                <b-col>
                  <b-form-group label="" label-for="create-analytics">
                    <b-btn
                      id="create-analytics"
                      class="create-button"
                      title="Create Analytics"
                      size="sm"
                      :disabled="isLoading"
                      @click="fetchSettlementData"
                    >
                      <b-spinner small v-if="isLoading"></b-spinner>
                      <span v-if="isLoading">Creazione in corso...</span>
                      <span v-else>Crea Analytics</span>
                    </b-btn>

                    <b-btn
                      v-if="showExportButton"
                      id="create-xslx"
                      class="create-button"
                      title="Esporta in Excel"
                      size="sm"
                      :disabled="isLoadingExport"
                      @click="exportToExcel"
                    >
                      <b-spinner small v-if="isLoadingExport"></b-spinner>
                      <span v-if="isLoadingExport"
                        >Esportazione in corso...</span
                      >
                      <span v-else>Esporta in Excel</span>
                    </b-btn>
                  </b-form-group>
                </b-col>
                <b-col sm="3">
                  <b-form-group label="" label-for="create-xslx">
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>
            <b-card title="Filtri di Ricerca" v-if="showExportButton">
              <b-form @submit.prevent="applyFilters" class="mb-3">
                <b-row>
                  <b-col md="2">
                    <b-form-group label="">
                      <b-form-input
                        v-model="columnFilters.flowDate"
                        placeholder="DD/MM/YYYY"
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col md="2">
                    <b-form-group label="">
                      <b-form-input
                        v-model="columnFilters.pod"
                        placeholder="Filter POD"
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col md="2">
                    <b-form-group label="">
                      <b-form-input
                        v-model="columnFilters.bspId"
                        placeholder="Filter BSP ID"
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col md="2">
                    <b-form-group label="">
                      <b-form-input
                        v-model="columnFilters.bspName"
                        placeholder="Filter BSP Name"
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col md="2">
                    <b-button type="submit" class="create-button" size="sm"
                      >Filtra</b-button
                    >
                    <b-button
                      type="button"
                      class="create-button"
                      size="sm"
                      @click="resetFilters"
                      :disabled="
                        isInitialLoad &&
                        !Object.values(columnFilters).some(
                          (filter) => filter !== ''
                        )
                      "
                    >
                      Reset
                    </b-button>
                  </b-col>
                </b-row>
              </b-form>
            </b-card>
            <!-- Tabella per i dati normali (status 200 o All) -->
            <b-table
              v-if="selectedStatus !== 422"
              ref="dataTable"
              responsive
              striped
              hover
              :items="filteredResults"
              :fields="fields"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :current-page="currentPage"
              :per-page="perPage"
              @filtered="onFiltered"
            >
              <template #cell(creationDate)="data">
                {{ formatDate(data.item.creationDate) }}
              </template>
              <template #cell(flowDate)="data">
                {{ formatDate(data.item.flowDate) }}
              </template>
              <template #cell(movements)="data">
                <b-button
                  v-b-modal="
                    `modal-${data.item.marketSession}-${data.item.pod}`
                  "
                  size="sm"
                  >View Movements</b-button
                >
                <b-modal
                  :id="`modal-${data.item.marketSession}-${data.item.pod}`"
                  title="Movements"
                  size="xl"
                  hide-footer
                  ok-only
                >
                  <b-table
                    :items="data.item.movements"
                    :fields="movementFields"
                    striped
                    small
                  >
                  </b-table>
                </b-modal>
              </template>
              <template #cell(errors)="data">
                <b-button
                  v-if="data.item.errors && data.item.errors.length"
                  v-b-modal="
                    `modal-errors-${data.item.marketSession}-${data.item.pod}`
                  "
                  size="sm"
                  variant="warning"
                >
                  View Errors
                </b-button>
                <b-modal
                  :id="`modal-errors-${data.item.marketSession}-${data.item.pod}`"
                  :title="`Errors Messages - ${data.item.pod} - ${formatDate(
                    data.item.flowDate
                  )}`"
                  size="xl"
                  hide-footer
                  ok-only
                >
                  <b-table
                    :items="
                      (data.item.errors || []).map((error, index) => ({
                        id: index + 1,
                        message: error.message,
                      }))
                    "
                  >
                    :fields="[ { key: 'id', label: '#' }, { key: 'message',
                    label: 'Error Message' } ]" striped small>
                  </b-table>
                  <div
                    class="text-right mt-3"
                    v-if="
                      data.item.errors &&
                      data.item.errors.some((error) => error.type === 'measure')
                    "
                  >
                    <b-button
                      variant="primary"
                      @click="retrieveMeasures(data.item, currentPage)"
                    >
                      Retrieve Measures
                    </b-button>
                  </div>
                </b-modal>
              </template>
            </b-table>

            <!--  <b-table
            v-if="selectedStatus === 422"
            responsive
            striped
            hover
            :items="errorResults"
            :fields="errorFields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :current-page="currentPage"
            :per-page="perPage"
            :filter="filter"
            @filtered="onFiltered"
          >
            <template #cell(message)="data">
              {{ data.value }}
            </template>
          </b-table> -->

            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              align="center"
              class="my-0"
            ></b-pagination>
          </b-card>
        </b-tab>
        <b-tab title="MGP">
          <b-card
            header-class="mgp-class"
            header="Settlement MGP"
            header-text-variant="white"
            header-tag="header"
          >
            <b-card>
              <b-row class="my-1">
                <b-col sm="3">
                  <b-form-select
                    id="year-select"
                    v-model="selectedYear"
                    :options="yearOptions"
                    @change="validateDates"
                  ></b-form-select>
                </b-col>
                <b-col sm="3">
                  <b-form-select
                    id="month-select"
                    v-model="selectedMonth"
                    :options="monthOptions"
                    @change="validateDates"
                  ></b-form-select>
                </b-col>
                <b-col>
                  <b-form-group label="" label-for="create-mgp">
                    <b-btn
                      id="create-mgp"
                      class="create-button"
                      title="Create MGP"
                      size="sm"
                      :disabled="isLoadingMgp"
                      @click="fetchSettlementMgpData"
                    >
                      <b-spinner small v-if="isLoadingMgp"></b-spinner>
                      <span v-if="isLoadingMgp">Creazione in corso...</span>
                      <span v-else>Crea MGP</span>
                    </b-btn>

                    <b-btn
                      v-if="showExportButtonMgp"
                      id="create-xslx"
                      class="create-button"
                      title="Esporta in Excel"
                      size="sm"
                      :disabled="isLoadingExportMgp"
                      @click="exportToExcelMgp"
                    >
                      <b-spinner small v-if="isLoadingExportMgp"></b-spinner>
                      <span v-if="isLoadingExportMgp"
                        >Esportazione in corso...</span
                      >
                      <span v-else>Esporta in Excel</span>
                    </b-btn>
                    <b-btn
                      v-if="showDownloadXmlButton"
                      id="download-xml"
                      class="create-button"
                      size="sm"
                      :disabled="isLoadingXml || hasErrorsMgp || hasErrorsMtf"
                      @click="downloadXml"
                      v-b-tooltip.hover
                      :title="mgpTooltipTextDownload"
                    >
                      <b-spinner small v-if="isLoadingXml"></b-spinner>
                      <span v-if="isLoadingXml">Download in corso...</span>
                      <span v-else>Scarica XML</span>
                    </b-btn>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group label="" label-for="create-mgp">
                    <b-btn
                      v-if="showDownloadXmlButton"
                      id="send-xml"
                      class="create-button"
                      size="sm"
                      :disabled="isMgpXmlSendDisabled || hasErrorsMgp || hasErrorsMtf"
                      @click="sendMgpXmlToGme"
                      v-b-tooltip.hover
                      :title="mgpTooltipTextSend"
                    >
                      <b-spinner small v-if="isLoadingXmlGme"></b-spinner>
                      <span v-if="isLoadingXmlGme">Invio in corso...</span>
                      <span v-else>Invia XML al GME</span>
                    </b-btn>
                  </b-form-group>
                </b-col>
                <b-col>
                  <div class="alert-message text-danger font-weight-bold"
                        v-if="showDownloadXmlButton && (hasErrorsMgp || hasErrorsMtf)">
                    <b-icon icon="exclamation-triangle"></b-icon> Data Errors
                  </div>
                </b-col>
              </b-row>
            </b-card>
            <b-table
              v-if="selectedStatus !== 422"
              ref="dataTableMgp"
              responsive
              striped
              hover
              :items="flattenedMgpResults"
              :fields="movementMgpFields"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :current-page="currentPage"
              :per-page="perPage"
              :filter="filter"
              @filtered="onFilteredMgp"
            >
            </b-table>

            <b-table
              v-if="selectedStatus === 422"
              responsive
              striped
              hover
              :items="errorResultsMgp"
              :fields="errorFields"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :current-page="currentPage"
              :per-page="perPage"
              :filter="filterMgp"
              @filtered="onFilteredMgp"
            >
              <template #cell(message)="data">
                {{ data.value }}
              </template>
            </b-table>

            <b-pagination
              v-model="currentPage"
              :total-rows="totalRowsMgp"
              :per-page="perPage"
              align="center"
              class="my-0"
            ></b-pagination>
          </b-card>
        </b-tab>

        <b-tab title="MTF">
          <b-card
            header="Settlement MTF"
            header-text-variant="white"
            header-tag="header"
            header-class="mtf-class"
          >
            <b-card>
              <b-row class="my-1">
                <b-col sm="3">
                  <b-form-select
                    id="year-select-mtf"
                    v-model="selectedYearMtf"
                    :options="yearOptionsMtf"
                    @change="validateDates"
                  ></b-form-select>
                </b-col>
                <b-col sm="3">
                  <b-form-select
                    id="month-select-mtf"
                    v-model="selectedMonthMtf"
                    :options="monthOptionsMtf"
                    @change="validateDates"
                  ></b-form-select>
                </b-col>
                <b-col>
                  <b-form-group label="" label-for="create-mtf">
                    <b-btn
                      id="create-mtf"
                      class="create-button"
                      title="Crea MTF"
                      size="sm"
                      :disabled="isLoadingMtf"
                      @click="fetchSettlementMtfData"
                    >
                      <b-spinner small v-if="isLoadingMtf"></b-spinner>
                      <span v-if="isLoadingMtf">Creazione in corso...</span>
                      <span v-else>Crea MTF</span>
                    </b-btn>

                    <b-btn
                      v-if="showExportButtonMtf"
                      id="create-xslx-mtf"
                      class="create-button"
                      title="Esporta in Excel"
                      size="sm"
                      :disabled="isLoadingExportMtf"
                      @click="exportToExcelMtf"
                    >
                      <b-spinner small v-if="isLoadingExportMtf"></b-spinner>
                      <span v-if="isLoadingExportMtf"
                        >Esportazione in corso...</span
                      >
                      <span v-else>Esporta in Excel</span>
                    </b-btn>
                    <b-btn
                      v-if="showDownloadXmlButtonMtf"
                      id="download-xml-mtf"
                      class="create-button"
                      size="sm"
                      :disabled="isLoadingXmlMtf || hasErrorsMtf"
                      @click="downloadXmlMtf"
                      v-b-tooltip.hover
                      :title="mtfTooltipTextDownload"
                    >
                      <b-spinner small v-if="isLoadingXmlMtf"></b-spinner>
                      <span v-if="isLoadingXmlMtf">Download in corso...</span>
                      <span v-else>Scarica XML</span>
                    </b-btn>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group label="" label-for="create-mtf">
                    <b-btn
                      v-if="showDownloadXmlButtonMtf"
                      id="send-xml-mtf"
                      class="create-button"
                      size="sm"
                      :disabled="isMtfXmlSendDisabled || hasErrorsMtf"
                      @click="sendMtfXmlToGme"
                      v-b-tooltip.hover
                      :title="mtfTooltipTextSend"
                    >
                      <b-spinner small v-if="isLoadingXmlGmeMtf"></b-spinner>
                      <span v-if="isLoadingXmlGmeMtf">Invio in corso...</span>
                      <span v-else>Invia XML al GME</span>
                    </b-btn>
                  </b-form-group>
                </b-col>
                <b-col>
                  <div class="alert-message text-danger font-weight-bold"
                    v-if="showDownloadXmlButtonMtf && hasErrorsMtf">
                    <b-icon icon="exclamation-triangle"></b-icon> Data Errors
                  </div>
                </b-col>
              </b-row>
            </b-card>

            <b-table
              v-if="selectedStatus !== 422"
              responsive
              striped
              hover
              :items="flattenedMtfResults"
              :fields="movementMtfFields"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :current-page="currentPage"
              :per-page="perPage"
              :filter="filter"
              @filtered="onFiltered"
            >
            </b-table>

            <b-table
              v-if="selectedStatus === 422"
              responsive
              striped
              hover
              :items="errorResultsMtf"
              :fields="errorFields"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :current-page="currentPage"
              :per-page="perPage"
              :filter="filter"
              @filtered="onFiltered"
            >
              <template #cell(message)="data">
                {{ data.value }}
              </template>
            </b-table>

            <b-pagination
              v-model="currentPage"
              :total-rows="totalRowsMtf"
              :per-page="perPage"
              align="center"
              class="my-0"
            ></b-pagination>
          </b-card>
        </b-tab>
      </b-tabs>
    </b-card>
  </b-container>
</template>
<style>
.bg-warning {
  background-color: #b70b6d !important;
}

.mgp-class {
  background-color: #eb6610 !important;
}

.mtf-class {
  background-color: #0a94d1 !important;
}

.text-white {
  color: #fff !important;
  font-size: 1.5em;
}
</style>
<script>
import SettlementService from "@/services/SettlementService"
import { utils, writeFile } from "xlsx"

export default {
  name: "Settlement",
  data () {
    return {
      startDate: new Date().toISOString().split("T")[0],
      endDate: new Date().toISOString().split("T")[0],
      selectedStatus: null,
      results: [],
      errorResults: [],
      resultsMgp: [],
      errorResultsMgp: [],
      resultsMtf: [],
      errorResultsMtf: [],
      fields: [
        { key: "flowDate", label: "Flow Date", sortable: true },
        { key: "pod", label: "POD", sortable: true },
        { key: "serviceId", label: "Service ID", sortable: true },
        { key: "bspId", label: "BSP ID", sortable: true },
        { key: "bspName", label: "BSP Name", sortable: true },
        // { key: 'auctionQuantity', label: 'Quantità Asta (Mw)', sortable: true },
        { key: "productCode", label: "Product Code", sortable: true },
        { key: "offerId", label: "Offer Id", sortable: true },
        { key: "errors", label: "Errori" },
        { key: "movements", label: "Movements" }
      ],
      errorFields: [
        { key: "marketSessionStartTime", label: "Flow Date", sortable: true },
        { key: "pod", label: "POD", sortable: true },
        { key: "message", label: "Error Message", sortable: true }
      ],
      movementFields: [
        { key: "index", label: "Index" },
        { key: "measuredP", label: "Measured P" },
        { key: "baselinePostP", label: "Baseline Post P" },
        { key: "requestQuantity", label: "Request Quantity (Mw)" },
        { key: "validatedQuantity", label: "Validated Quantity (Mw)" },
        { key: "realQuantity", label: "Real Quantity (Mw)" },
        { key: "settlementQuantity", label: "Settlement Quantity (Mw)" },
        { key: "CK_SOGLIA_MINIMA", label: "CK SOGLIA MINIMA" },
        { key: "PERC_REAL_VALID", label: "% REAL / VALID" }
      ],
      movementMgpFields: [
        { key: "market", label: "Market", sortable: true },
        { key: "flowDate", label: "Flow Date", sortable: true },
        { key: "bspId", label: "BSP ID", sortable: true },
        { key: "offerId", label: "Offer Id", sortable: true },
        { key: "availableDate", label: "Available Date" },
        // { key: 'availableQty', label: 'Available Qty' },
        // { key: 'availableEnergy', label: 'Available Energy' },
        { key: "undispensedQty", label: "Undispensed Qty (Mw)" },
        { key: "purpose", label: "Purpose" }
      ],
      errorFieldsMgp: [
        { key: "marketSessionStartTime", label: "Flow Date", sortable: true },
        { key: "bspId", label: "BSP Id", sortable: true },
        { key: "message", label: "Error Message", sortable: true }
      ],
      movementMtfFields: [
        { key: "market", label: "Market", sortable: true },
        { key: "bspName", label: "BSP Name", sortable: true },
        { key: "productCode", label: "Product Code", sortable: true },
        { key: "bspId", label: "BSP ID", sortable: true },
        { key: "offerId", label: "Offer Id", sortable: true },
        // { key: 'flowDate', label: 'Flow Date', sortable: true },
        { key: "availableDate", label: "Available Date" },
        { key: "availableQty", label: "Available Qty (Mw)" },
        // { key: 'availableEnergy', label: 'Available Energy' },
        { key: "purpose", label: "Purpose" }
      ],
      errorFieldsMtf: [
        { key: "marketSessionStartTime", label: "Flow Date", sortable: true },
        { key: "bspId", label: "BSP Id", sortable: true },
        { key: "message", label: "Error Message", sortable: true }
      ],
      sortBy: "flowDate",
      sortDesc: false,
      currentPage: 1,
      perPage: 20,
      filter: null,
      filterMgp: null,
      isLoading: false,
      isLoadingExport: false,
      isLoadingMgp: false,
      isLoadingExportMgp: false,
      selectedYear: new Date().getFullYear().toString(),
      selectedMonth: (new Date().getMonth() + 1).toString().padStart(2, "0"),
      selectedYearMtf: new Date().getFullYear().toString(),
      selectedMonthMtf: (new Date().getMonth() + 1).toString().padStart(2, "0"),
      yearOptions: this.generateYearOptions(),
      monthOptions: this.generateMonthOptions(),
      yearOptionsMtf: this.generateYearOptions(),
      monthOptionsMtf: this.generateMonthOptions(),
      isLoadingXml: false,
      hasErrorsMgp: false,
      hasErrorsMtf: false,
      xmlData: null,
      xmlDataMtf: null,
      isLoadingXmlGme: false,
      isLoadingXmlMtf: false,
      isLoadingXmlGmeMtf: false,
      isLoadingMtf: false,
      isLoadingExportMtf: false,
      columnFilters: {
        flowDate: "",
        pod: "",
        bspId: "",
        bspName: ""
      },
      columnFiltersMgp: {
        flowDate: "",
        bspId: ""
      },
      filteredResults: [],
      filteredResultsMgp: [],
      isInitialLoad: true,
      isInitialLoadMgp: true,
      mgpTransmissionDate: null,
      mtfTransmissionDate: null
    }
  },
  computed: {
    flattenedResults () {
      if (!Array.isArray(this.results) || this.results.length === 0) {
        return []
      }
      return this.results.flatMap((item) => {
        if (!item.pods || !Array.isArray(item.pods)) {
          console.warn("item.pods is not an array:", item)
          return []
        }
        return item.pods.map((pod) => ({
          ...item,
          ...pod,
          errors: pod.errors,
          movements: pod.movements
        }))
      })
    },
    flattenedMgpResults () {
      if (!Array.isArray(this.resultsMgp) || this.resultsMgp.length === 0) {
        return []
      }

      return this.resultsMgp.flatMap((item) => {
        if (!item.movements || !Array.isArray(item.movements)) {
          console.warn("item.movements is not an array:", item)
          return []
        }

        return item.movements.map((movement) => ({
          market: item.market,
          flowDate: this.formatDate(item.flowDate),
          bspId: movement.bspId,
          offerId: movement.offerId,
          availableDate: this.formatDate(movement.availableDate),
          availableQty: this.wattToMegawatt(movement.availableQty),
          availableEnergy: this.wattToMegawatt(movement.availableEnergy),
          undispensedQty: this.wattToMegawatt(movement.undispensedQty),
          purpose: movement.purpose
        }))
      })
    },
    getUndispensedQtyOrError () {
      return (movement) => movement.error ? "Error presence" : movement.undispensedQty || ""
    },
    flattenedMtfResults () {
      if (!Array.isArray(this.resultsMtf) || this.resultsMtf.length === 0) {
        return []
      }

      return this.resultsMtf.flatMap((item) => {
        if (!item.productCodes || !Array.isArray(item.productCodes)) {
          console.warn("item.productCodes is not an array:", item)
          return []
        }

        return item.productCodes.flatMap((productCode) => {
          if (!productCode.movements || !Array.isArray(productCode.movements)) {
            console.warn("productCode.movements is not an array:", productCode)
            return []
          }

          return productCode.movements.map((movement) => ({
            market: productCode.market,
            flowDate: this.formatDate(item.flowDate),
            productCode: productCode.productCode,
            bspId: movement.bspId,
            bspName: movement.bspName,
            offerId: movement.offerId,
            index: movement.index,
            availableDate: this.formatDate(movement.availableDate),
            availableQty: this.wattToMegawatt(movement.availableQty),
            availableEnergy: this.wattToMegawatt(movement.availableEnergy),
            purpose: movement.purpose
          }))
        })
      })
    },
    totalRows: {
      get () {
        return !this.isInitialLoad && this._totalRows !== undefined
          ? this._totalRows
          : this.selectedStatus === 422
            ? this.errorResults.length
            : this.flattenedResults.length
      },
      set (valore) {
        this._totalRows = valore
      }
    },

    totalRowsMgp: {
      get () {
        return this.selectedStatus === 422
          ? this.errorResultsMgp.length
          : this.flattenedMgpResults.length
      },
      set (valore) {
        this._totalRowsMgp = valore
      }
    },

    totalRowsMtf: {
      get () {
        return this.selectedStatus === 422
          ? this.errorResultsMtf.length
          : this.flattenedMtfResults.length
      },
      set (valore) {
        this._totalRowsMtf = valore
      }
    },
    showExportButton () {
      return this.selectedStatus !== 422 && this.flattenedResults.length > 0
    },
    showExportButtonMgp () {
      return this.selectedStatus !== 422 && this.flattenedMgpResults.length > 0
    },
    showExportButtonMtf () {
      return this.selectedStatus !== 422 && this.flattenedMtfResults.length > 0
    },
    showDownloadXmlButton () {
      return this.flattenedMgpResults.length > 0
    },
    showXmlToGmeButton () {
      return this.flattenedMgpResults.length > 0
    },
    showDownloadXmlButtonMtf () {
      return this.flattenedMtfResults.length > 0
    },
    showXmlToGmeButtonMtf () {
      return this.flattenedMtfResults.length > 0
    },
    isMgpXmlSendDisabled () {
      return this.mgpTransmissionDate !== null
    },

    isMtfXmlSendDisabled () {
      return this.mtfTransmissionDate !== null
    },

    mgpTooltipTextSend () {
      if (this.mgpTransmissionDate) {
        return `MGP inviato in data ${this.formatDate(
          this.mgpTransmissionDate
        )}`
      } else if (this.hasErrorsMgp) {
        return `Some record with errors`
      }
      return "Invia XML al GME"
    },
    mgpTooltipTextDownload () {
      if (this.hasErrorsMgp) {
        return `Some record with errors`
      }
      return "Scarica XML"
    },
    mtfTooltipTextDownload () {
      if (this.hasErrorsMtf) {
        return `Some record with errors`
      }
      return "Scarica XML"
    },
    mtfTooltipTextSend () {
      if (this.mtfTransmissionDate) {
        return `MTF inviato in data ${this.formatDate(
          this.mtfTransmissionDate
        )}`
      } else if (this.hasErrorsMtf) {
        return `Some record with errors`
      }
      return "Invia XML al GME"
    }
  },
  mounted () {},
  methods: {
    async fetchSettlementData () {
      this.isLoading = true
      this.resetFilters()
      try {
        const params = {
          start: this.startDate,
          end: this.endDate
        }
        const convertedData = await SettlementService.settlement(params)
        const response = this.convertToMegawattAnalytics(convertedData)
        console.log("RESPONSE >>", response)

        if (Array.isArray(response)) {
          this.results = response
          this.errorResults = []
          this.selectedStatus = 200
        } else if (response && response.status === 422) {
          this.results = []
          this.selectedStatus = 422
          const errorMessage = response.errors

          this.$bvToast.toast(errorMessage, {
            title: "Errore",
            variant: "danger",
            solid: true
          })

          /* if (Array.isArray(response.errors) && typeof response.errors[0] === 'object') {
            this.errorResults = response.errors.map(error => ({
              marketSessionStartTime: this.formatDate(error.marketSessionStartTime),
              pod: error.pod,
              message: error.message
            }))
          } else if (Array.isArray(response.errors) && typeof response.errors[0] === 'string') {
            this.errorResults = response.errors.map(error => ({
              marketSessionStartTime: '',
              pod: '',
              message: error
            }))
          } else if (typeof response.errors === 'string') {
            this.errorResults = [{
              marketSessionStartTime: '',
              pod: '',
              message: response.errors
            }]
          } else {
            console.error('Formato di errore non riconosciuto:', response.errors)
            this.errorResults = [{
              marketSessionStartTime: '',
              pod: '',
              message: 'Errore non specificato'
            }]
          } */
        } else if (response && response.data) {
          this.results = Array.isArray(response.data)
            ? response.data
            : [response.data]
          this.errorResults = []
          this.selectedStatus = 200
        } else {
          console.error("Unexpected response format:", response)
          this.results = []
          this.errorResults = []
          this.selectedStatus = null
        }
        console.log("RESPONSE >>", response)
        this.totalRows =
          this.selectedStatus === 422
            ? this.errorResults.length
            : this.results.length

        console.log("Results:", this.results)
        console.log("Error Results:", this.errorResults)
        console.log("Selected Status:", this.selectedStatus)
        console.log("Total Rows:", this.totalRows)
      } catch (error) {
        console.error("Error fetching settlement data:", error)
        this.results = []
        this.errorResults = []
        this.totalRows = 0
        this.selectedStatus = null
      } finally {
        this.isLoading = false
      }
    },
    async fetchSettlementMgpData () {
      this.isLoadingMgp = true
      this.hasErrorsMgp = false
      this.hasErrorsMtf = false
      try {
        const params = {
          year: this.selectedYear,
          month: this.selectedMonth
        }
        const response = await SettlementService.settlementMGP(params)
        console.log("RESPONSE >>", response)

        if (response.header && response.header.transmissionDate) {
          this.mgpTransmissionDate = response.header.transmissionDate
        }

        if (
          response.status === 422 ||
          (response.data && response.data.status === 422)
        ) {
          console.log("RESPONSE status 422")
          this.resultsMgp = []
          this.selectedStatus = 422
          const errorData =
            response.errors || (response.data && response.data.errors) || []
          console.log("RESPONSE 422>>", errorData)

          if (Array.isArray(errorData) && typeof errorData[0] === "object") {
            this.errorResultsMgp = errorData.map((error) => ({
              marketSessionStartTime: this.formatDate(
                error.marketSessionStartTime
              ),
              pod: error.pod,
              message: error.message
            }))
          } else if (
            Array.isArray(errorData) &&
            typeof errorData[0] === "string"
          ) {
            this.errorResultsMgp = errorData.map((error) => ({
              marketSessionStartTime: "",
              pod: "",
              message: error
            }))
          } else if (typeof errorData === "string") {
            this.errorResultsMgp = [
              {
                marketSessionStartTime: "",
                pod: "",
                message: errorData
              }
            ]
          } else {
            console.error("Formato di errore non riconosciuto:", errorData)
            this.errorResultsMgp = [
              {
                marketSessionStartTime: "",
                pod: "",
                message: "Errore non specificato"
              }
            ]
          }
        } else if (response.header && response.marketSessions) {
          const { header, marketSessions } = response
          this.header = header
          this.resultsMgp = marketSessions
          this.errorResultsMgp = []
          this.selectedStatus = 200
          // Start Settlement base
          this.hasErrorsMgp = marketSessions.some(session =>
            session.movements && session.movements.some(movement => movement.error)
          )
          console.log(this.hasErrorsMgp ? "Ci sono errori nei dati" : "Tutti i dati sono privi di errori")
          // End Settlement base
        } else if (response.data) {
          this.resultsMgp = Array.isArray(response.data)
            ? response.data
            : [response.data]
          this.errorResultsMgp = []
          this.selectedStatus = 200
        } else {
          console.error("Unexpected response format:", response)
          this.resultsMgp = []
          this.errorResultsMgp = []
          this.selectedStatus = null
        }

        console.log("RESPONSE >>", response)
        this.errorResultsMgp = this.errorResultsMgp || []
        this.resultsMgp = this.resultsMgp || []

        this.totalRowsMgp =
          this.selectedStatus === 422
            ? this.errorResultsMgp.length
            : this.resultsMgp.length
        console.log("Selected Status:", this.selectedStatus)
        console.log("Total Rows:", this.totalRowsMgp)
      } catch (error) {
        console.error("Error fetching settlement data:", error)
        this.resultsMgp = []
        this.errorResultsMgp = []
        this.totalRowsMgp = 0
        this.selectedStatus = null
      } finally {
        this.isLoadingMgp = false
      }
    },
    async fetchSettlementMtfData () {
      this.isLoadingMtf = true
      this.hasErrorsMgp = false
      this.hasErrorsMtf = false
      try {
        const params = {
          year: this.selectedYearMtf,
          month: this.selectedMonthMtf
        }
        const response = await SettlementService.settlementMtf(params)
        console.log("RESPONSE >>", response)

        if (response.header && response.header.transmissionDate) {
          this.mtfTransmissionDate = response.header.transmissionDate
        }

        if (
          response.status === 422 ||
          (response.data && response.data.status === 422)
        ) {
          this.resultsMtf = []
          this.selectedStatus = 422
          const errorData =
            response.errors || (response.data && response.data.errors) || []

          if (Array.isArray(errorData) && typeof errorData[0] === "object") {
            this.errorResultsMtf = errorData.map((error) => ({
              marketSessionStartTime: this.formatDate(
                error.marketSessionStartTime
              ),
              pod: error.pod,
              message: error.message
            }))
          } else if (
            Array.isArray(errorData) &&
            typeof errorData[0] === "string"
          ) {
            this.errorResultsMtf = errorData.map((error) => ({
              marketSessionStartTime: "",
              pod: "",
              message: error
            }))
          } else if (typeof errorData === "string") {
            this.errorResultsMtf = [
              {
                marketSessionStartTime: "",
                pod: "",
                message: errorData
              }
            ]
          } else {
            console.error("Formato di errore non riconosciuto:", errorData)
            this.errorResultsMtf = [
              {
                marketSessionStartTime: "",
                pod: "",
                message: "Errore non specificato"
              }
            ]
          }
        } else if (response.header && response.marketSessions) {
          const { header, marketSessions } = response
          this.header = header
          this.resultsMtf = marketSessions
          this.errorResultsMtf = []
          this.selectedStatus = 200
          // Start Settlement base
          this.hasErrorsMtf = marketSessions.some(session =>
            session.productCodes && session.productCodes.some(productCode =>
              productCode.movements && productCode.movements.some(movement => movement.error)
            )
          )
          console.log(this.hasErrorsMtf ? "Ci sono errori nei dati" : "Tutti i dati sono privi di errori")
          // End Settlement base
        } else if (response.data) {
          this.resultsMtf = Array.isArray(response.data)
            ? response.data
            : [response.data]
          this.errorResultsMtf = []
          this.selectedStatus = 200
        } else {
          console.error("Unexpected response format:", response)
          this.resultsMtf = []
          this.errorResultsMtf = []
          this.selectedStatus = null
        }

        console.log("RESPONSE >>", response)
        this.errorResultsMtf = this.errorResultsMtf || []
        this.resultsMtf = this.resultsMtf || []

        this.totalRowsMtf =
          this.selectedStatus === 422
            ? this.errorResultsMtf.length
            : this.resultsMtf.length
        console.log("Selected Status:", this.selectedStatus)
        console.log("Total Rows:", this.totalRowsMtf)
      } catch (error) {
        console.error("Error fetching settlement data:", error)
        this.resultsMtf = []
        this.errorResultsMtf = []
        this.totalRowsMtf = 0
        this.selectedStatus = null
      } finally {
        this.isLoadingMtf = false
      }
    },
    setSelectedStatus (status) {
      this.selectedStatus = status
      this.currentPage = 1
    },
    exportToExcel () {
      this.isLoadingExport = true
      // Chromium throws an error when handling large data sets in [flattenedResults].
      // console.log("FLATTENED RESULT >>> " + JSON.stringify(this.flattenedResults))
      try {
        const dataToExport = this.flattenedResults.flatMap((item) => {
          const baseData = {
            pod: item.pod,
            flowDate: this.formatDate(item.flowDate),
            serviceId: item.serviceId,
            bspId: item.bspId,
            bspName: item.bspName,
            auctionQuantity: item.auctionQuantity,
            productCode: item.productCode,
            offerId: item.offerId
          }

          if (!item.movements || item.movements.length === 0) {
            return [
              {
                ...baseData,
                index: "",
                measuredP: "",
                baselinePostP: "",
                requestQuantity: "",
                validatedQuantity: "",
                realQuantity: "",
                settlementQuantity: "",
                CK_SOGLIA_MINIMA: "",
                PERC_REAL_VALID: "",
                errors: ""
              }
            ]
          }

          return item.movements.map((movement) => {
            // Filtra solo gli errori relativi all'indice specifico o errori generali del pod
            let errorMessage = ""

            if (Array.isArray(item.errors)) {
              // Filtra errori specifici per l'indice corrente
              const specificErrors = item.errors.filter(err =>
                (err.context === 'index' && err.index === movement.index) ||
            (err.context === 'pod' && !err.index)
              )

              if (specificErrors.length > 0) {
                errorMessage = specificErrors.map(err => err.message).join('; ')
              }
            } else if (item.errors && item.errors.message) {
              errorMessage = item.errors.message
            }

            return {
              ...baseData,
              index: movement.index,
              measuredP: movement.measuredP,
              baselinePostP: movement.baselinePostP,
              requestQuantity: movement.requestQuantity,
              validatedQuantity: movement.validatedQuantity,
              realQuantity: movement.realQuantity,
              settlementQuantity: movement.settlementQuantity,
              CK_SOGLIA_MINIMA: movement.CK_SOGLIA_MINIMA,
              PERC_REAL_VALID: movement.PERC_REAL_VALID,
              errors: errorMessage
            }
          })
        })

        const wb = utils.book_new()
        const ws = utils.json_to_sheet(dataToExport)

        const percRealValidIndex = Object.keys(dataToExport[0]).indexOf("PERC_REAL_VALID")

        const range = utils.decode_range(ws["!ref"])
        for (let R = range.s.r + 1; R <= range.e.r; ++R) {
          const cellRef = utils.encode_cell({ r: R, c: percRealValidIndex })
          if (!ws[cellRef]) continue
          let cellValue = ws[cellRef].v
          if (typeof cellValue === "string" && cellValue.endsWith("%")) {
            let numericValue = parseFloat(cellValue.slice(0, -1)) / 100
            ws[cellRef].v = numericValue
            ws[cellRef].t = "n"
            ws[cellRef].z = "0%"
          }
        }
        utils.book_append_sheet(wb, ws, "Dati Settlement")

        writeFile(wb, "dati_settlement_analytics.xlsx")
      } catch (error) {
        console.error("Errore nella generazione del file:", error)
      } finally {
        this.isLoadingExport = false
      }
    },

    exportToExcelMgp () {
      this.isLoadingExportMgp = true
      try {
        const headers = [
          { header: "Market", key: "market", width: 25 },
          // { header: 'Flow Date', key: 'flowDate', width: 12 },
          { header: "BSP ID", key: "bspId", width: 15 },
          { header: "Offer Id", key: "offerId", width: 10 },
          { header: "Available Date", key: "availableDate", width: 12 },
          { header: "Available Time", key: "availableTime", width: 10 },
          { header: "Available Qty", key: "availableQty", width: 15 },
          { header: "Available Energy", key: "availableEnergy", width: 15 },
          { header: "Undispensed Qty", key: "undispensedQty", width: 15 },
          { header: "Purpose", key: "purpose", width: 10 }
        ]

        const dataToExport = this.flattenedMgpResults.map((item) => {
          console.log("AVAILABLE DATE >>> " + item.availableDate)
          const [availableDate, availableTime] = this.splitDateTime(
            item.availableDate
          )
          return {
            // flowDate: this.formatDate(item.flowDate),
            market: item.market,
            bspId: item.bspId,
            offerId: item.offerId,
            availableDate,
            availableTime,
            availableQty: item.availableQty,
            availableEnergy: item.availableEnergy,
            undispensedQty: (item.undispensedQty === undefined || item.undispensedQty === null || item.undispensedQty === '') ? "Data error" : item.undispensedQty,
            purpose: item.purpose
          }
        })
        const wb = utils.book_new()
        const ws = utils.json_to_sheet(dataToExport, {
          header: headers.map((h) => h.key)
        })

        ws["!cols"] = headers.map((h) => ({ wch: h.width }))

        utils.book_append_sheet(wb, ws, "Dati Settlement MGP")

        writeFile(wb, "dati_settlement_mgp.xlsx")
      } catch (error) {
        console.error("Errore nella generazione del file:", error)
      } finally {
        this.isLoadingExportMgp = false
      }
    },
    exportToExcelMtf () {
      this.isLoadingExportMtf = true
      try {
        const headers = [
          { header: "Market", key: "market", width: 10 },
          { header: "BSP Name", key: "bspName", width: 30 },
          { header: "Product Code", key: "productCode", width: 15 },
          { header: "BSP ID", key: "bspId", width: 15 },
          { header: "Offer Id", key: "offerId", width: 10 },
          { header: "Available Date", key: "availableDate", width: 12 },
          { header: "Available Time", key: "availableTime", width: 10 },
          { header: "Available Qty", key: "availableQty", width: 15 },
          { header: "Available Energy", key: "availableEnergy", width: 15 },
          { header: "Purpose", key: "purpose", width: 10 }
        ]

        const dataToExport = this.flattenedMtfResults.map((item) => {
          const [availableDate, availableTime] = this.splitDateTime(
            item.availableDate
          )
          return {
            market: item.market,
            bspName: item.bspName,
            // flowDate: this.formatDateMgp(item.flowDate),
            productCode: item.productCode,
            bspId: item.bspId,
            offerId: item.offerId,
            availableDate,
            availableTime,
            // availableQty: item.availableQty,
            availableQty: (item.availableQty === undefined || item.availableQty === null || item.availableQty === '') ? "Data error" : item.availableQty,
            availableEnergy: item.availableEnergy,
            purpose: item.purpose
          }
        })

        const wb = utils.book_new()
        const ws = utils.json_to_sheet(dataToExport, {
          header: headers.map((h) => h.key)
        })

        ws["!cols"] = headers.map((h) => ({ wch: h.width }))

        utils.book_append_sheet(wb, ws, "Dati Settlement MTF")

        writeFile(wb, "dati_settlement_mtf.xlsx")
      } catch (error) {
        console.error("Errore nella generazione del file:", error)
      } finally {
        this.isLoadingExportMtf = false
      }
    },

    splitDateTime (dateTimeString) {
      console.log("DATA STRING >> " + dateTimeString)
      if (!dateTimeString) return ["", ""]

      // Controlla se il formato è "DD/MM/YYYY HH:MM"
      const parts = dateTimeString.split(" ")
      if (parts.length === 2) {
        const [datePart, timePart] = parts
        const [day, month, year] = datePart.split("/")

        // Crea una data nel formato corretto per JavaScript
        const date = new Date(`${year}-${month}-${day}T${timePart}:00`)

        if (!isNaN(date.getTime())) {
          const dateFormatted = `${year}-${month.padStart(
            2,
            "0"
          )}-${day.padStart(2, "0")}`
          const timeFormatted = `${timePart}:00`
          return [dateFormatted, timeFormatted]
        }
      }

      // Se non corrisponde al formato atteso, prova con il metodo originale
      const date = new Date(dateTimeString)
      if (isNaN(date.getTime())) return ["", ""]

      const dateFormatted = date.toISOString().split("T")[0] // YYYY-MM-DD
      const timeFormatted = date.toTimeString().split(" ")[0] // HH:MM:SS
      return [dateFormatted, timeFormatted]
    },
    formatDateMgp (dateString) {
      if (!dateString) return ""
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return ""
      const padZero = (num) => num.toString().padStart(2, "0")
      const day = padZero(date.getDate())
      const month = padZero(date.getMonth() + 1)
      const year = date.getFullYear()
      const hours = padZero(date.getHours())
      const minutes = padZero(date.getMinutes())

      return `${year}/${month}/${day} ${hours}:${minutes}`
    },
    formatDate (dateString) {
      if (!dateString) return ""

      if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/.test(dateString)) {
        return dateString
      }

      const date = new Date(dateString)
      if (isNaN(date.getTime())) return ""

      const padZero = (num) => num.toString().padStart(2, "0")
      const day = padZero(date.getDate())
      const month = padZero(date.getMonth() + 1)
      const year = date.getFullYear()
      const hours = padZero(date.getHours())
      const minutes = padZero(date.getMinutes())

      return `${day}/${month}/${year} ${hours}:${minutes}`
    },
    formatDateNoTime (dateString) {
      if (!dateString) return ""

      if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/.test(dateString)) {
        return dateString
      }

      const date = new Date(dateString)
      if (isNaN(date.getTime())) return ""

      const padZero = (num) => num.toString().padStart(2, "0")
      const day = padZero(date.getDate())
      const month = padZero(date.getMonth() + 1)
      const year = date.getFullYear()

      return `${year}-${month}-${day}`
    },

    validateDates () {
      if (this.startDate && this.endDate) {
        const start = new Date(this.startDate)
        const end = new Date(this.endDate)

        if (end < start) {
          const errorMessage =
            "La data di fine non può essere precedente alla data di inizio."
          this.$bvToast.toast(errorMessage, {
            title: "Errore",
            variant: "danger",
            solid: true
          })
          this.endDate = ""
        }
      }
    },
    generateYearOptions () {
      const currentYear = new Date().getFullYear()
      return Array.from({ length: 5 }, (_, i) => ({
        value: (currentYear - i).toString(),
        text: (currentYear - i).toString()
      }))
    },
    generateMonthOptions () {
      return Array.from({ length: 12 }, (_, i) => ({
        value: (i + 1).toString().padStart(2, "0"),
        text: new Date(2000, i, 1).toLocaleString("default", { month: "long" })
      }))
    },
    async downloadXml () {
      this.isLoadingXml = true
      try {
        const response = await SettlementService.settlementMGPXml(
          this.selectedYear,
          this.selectedMonth
        )
        this.xmlData = response

        const blob = new Blob([this.xmlData], { type: "application/xml" })

        const url = window.URL.createObjectURL(blob)

        const a = document.createElement("a")
        a.style.display = "none"
        a.href = url
        a.download = `settlement_mgp_${this.selectedYear}_${this.selectedMonth}_IDARETI-MGP.xml`

        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } catch (error) {
        console.error("Errore durante il download dell'XML:", error)
      } finally {
        this.isLoadingXml = false
      }
    },
    async downloadXmlMtf () {
      this.isLoadingXmlMtf = true
      try {
        const response = await SettlementService.settlementMtfXml(
          this.selectedYearMtf,
          this.selectedMonthMtf
        )
        this.xmlDataMtf = response
        console.log("response >>> " + this.xmlDataMtf)

        const blob = new Blob([this.xmlDataMtf], { type: "application/xml" })

        const url = window.URL.createObjectURL(blob)

        const a = document.createElement("a")
        a.style.display = "none"
        a.href = url
        a.download = `settlement_mtf_${this.selectedYearMtf}_${this.selectedMonthMtf}_IDARETI-MTF.xml`

        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } catch (error) {
        console.error("Errore durante il download dell'XML:", error)
      } finally {
        this.isLoadingXmlMtf = false
      }
    },
    async sendMgpXmlToGme () {
      this.isLoadingXmlGme = true
      try {
        const params = {
          year: this.selectedYear,
          month: this.selectedMonth
        }
        const response = await SettlementService.sendMgpXmlToGme(params)

        if (response.status === 200) {
          this.$bvToast.toast(
            response.message || "XML inviato con successo a GME",
            {
              title: "Invio completato",
              variant: "success",
              solid: true
            }
          )
        } else {
          throw new Error(response.errors || "Errore sconosciuto")
        }
      } catch (error) {
        console.error("Errore durante l'invio dell'XML a GME:", error)
        this.$bvToast.toast(
          error.message || "Si è verificato un errore durante l'invio dell'XML",
          {
            title: "Errore",
            variant: "danger",
            solid: true
          }
        )
      } finally {
        this.isLoadingXmlGme = false
      }
    },
    async sendMtfXmlToGme () {
      this.isLoadingXmlGmeMtf = true
      try {
        const params = {
          year: this.selectedYearMtf,
          month: this.selectedMonthMtf
        }
        const response = await SettlementService.sendMtfXmlToGme(params)

        if (response.status === 200) {
          this.$bvToast.toast(
            response.message || "XML inviato con successo a GME",
            {
              title: "Invio completato",
              variant: "success",
              solid: true
            }
          )
        } else {
          throw new Error(response.errors || "Errore sconosciuto")
        }
      } catch (error) {
        console.error("Errore durante l'invio dell'XML a GME:", error)
        this.$bvToast.toast(
          error.message || "Si è verificato un errore durante l'invio dell'XML",
          {
            title: "Errore",
            variant: "danger",
            solid: true
          }
        )
      } finally {
        this.isLoadingXmlGmeMtf = false
      }
    },
    async retrieveMeasures (item, currentPage) {
      try {
        const params = {
          pod: item.pod,
          flowDate: encodeURIComponent(this.formatDateNoTime(item.flowDate))
        }
        const response = await SettlementService.retrieveMeasures(params)
        console.log("response.status ", response.status)
        console.log("response.error ", response.error)
        if (response.status === 200) {
          this.$bvToast.toast(response.message || "Data retrieved regularly", {
            title: "Recovery completed",
            variant: "success",
            autoHideDelay: 3000
          })
          this.$bvModal.hide(`modal-errors-${item.marketSession}-${item.pod}`)
          this.fetchSettlementData()
          this.currentPage = currentPage
        } else {
          if (response.status === 404) {
            this.$bvToast.toast(response.error || "POD not found.", {
              title: "Warning",
              variant: "warning",
              autoHideDelay: 3000
            })
          } else {
            if (response.status === 422) {
              this.$bvToast.toast(
                response.error || "Missing data in measuredP or baselineP",
                {
                  title: "Warning",
                  variant: "warning",
                  autoHideDelay: 3000
                }
              )
              this.$bvModal.hide(
                `modal-errors-${item.marketSession}-${item.pod}`
              )
              this.fetchSettlementData()
              this.currentPage = currentPage
            } else {
              this.$bvToast.toast("No new measures retrieved.", {
                title: "Warning",
                variant: "warning",
                autoHideDelay: 3000
              })
            }
          }
        }
      } catch (error) {
        console.error("Error during data recovery:", error)
        this.$bvToast.toast(
          error.message || "An error occurred during data recovery",
          {
            title: "Errore",
            variant: "danger",
            solid: true
          }
        )
      }
    },

    convertToMegawattAnalytics (data) {
      if (data.status !== 422) {
        if (!Array.isArray(data)) {
          console.error("Data is not an array:", data)
          return data
        }

        return data.map((item) => {
          if (!item.pods || !Array.isArray(item.pods)) {
            console.warn("Item does not have pods array:", item)
            return item
          }

          return {
            ...item,
            pods: item.pods.map((pod) => {
              if (!pod.movements || !Array.isArray(pod.movements)) {
                console.warn("Pod does not have movements array:", pod)
                return pod
              }

              return {
                ...pod,
                auctionQuantity: this.wattToMegawatt(pod.auctionQuantity),
                movements: pod.movements.map((movement) => {
                  const realQuantity = this.wattToMegawatt(
                    movement.realQuantity
                  )
                  const validatedQuantity = this.wattToMegawatt(
                    movement.validatedQuantity
                  )

                  let percRealValid = "N/A"
                  if (
                    validatedQuantity != null &&
                    validatedQuantity !== 0 &&
                    realQuantity != null
                  ) {
                    percRealValid =
                      Math.round((realQuantity / validatedQuantity) * 100) +
                      "%"
                  }

                  return {
                    ...movement,
                    requestQuantity: this.wattToMegawatt(
                      movement.requestQuantity
                    ),
                    validatedQuantity: validatedQuantity,
                    realQuantity: realQuantity,
                    settlementQuantity: this.wattToMegawatt(
                      movement.settlementQuantity
                    ),
                    CK_SOGLIA_MINIMA:
                      realQuantity != null && validatedQuantity != null
                        ? realQuantity < validatedQuantity
                          ? "CONTROLLARE SOGLIA MINIMA"
                          : "OK"
                        : "N/A",
                    PERC_REAL_VALID: percRealValid
                  }
                })
              }
            })
          }
        })
      } else return data
    },
    wattToMegawatt (watt) {
      if (watt === 0 || watt === null || watt === undefined || watt === "") {
        return watt
      }
      return watt / 1000000
    },
    applyFilters () {
      this.isInitialLoad = false
      this._totalRows = undefined
      const hasActiveFilters = Object.values(this.columnFilters).some(
        (filter) => filter !== ""
      )

      if (!hasActiveFilters) {
        this.filteredResults = this.flattenedResults
      } else {
        this.filteredResults = this.flattenedResults.filter((item) => {
          return Object.keys(this.columnFilters).every((key) => {
            const filter = this.columnFilters[key].toLowerCase()
            if (key === "flowDate") {
              const normalizedItemDate = this.normalizeDate(item[key])
              const normalizedFilterDate = this.normalizeDate(filter)
              if (!filter) return true
              // Gestisce il caso di data incompleta (es. "09/07" o "09")
              const filterParts = filter.split("/")
              if (filterParts.length === 2) {
                // Confronta solo mese e giorno
                return (
                  normalizedItemDate.substring(5) ===
                  normalizedFilterDate.substring(5)
                )
              } else if (
                filterParts.length === 1 &&
                filterParts[0].length <= 2
              ) {
                // Confronta solo il giorno
                return (
                  normalizedItemDate.substring(8) ===
                  normalizedFilterDate.substring(8)
                )
              }

              return (
                normalizedFilterDate === "" ||
                normalizedItemDate === normalizedFilterDate
              )
            } else {
              return (
                filter === "" ||
                (item[key] &&
                  item[key].toString().toLowerCase().includes(filter))
              )
            }
          })
        })
      }
      this.totalRows = this.filteredResults.length
      console.log("Total items after filtering:", this.totalRows)
      this.currentPage = 1
    },
    resetFilters () {
      // Resetta tutti i filtri
      Object.keys(this.columnFilters).forEach((key) => {
        this.columnFilters[key] = ""
      })
      // Ripristina i risultati originali
      this.filteredResults = this.flattenedResults
      this.currentPage = 1
      this.isInitialLoad = true
      this._totalRows = undefined
    },
    applyFiltersMg () {
      console.log("Applying filters:", JSON.stringify(this.columnFiltersMgp))
      console.log(
        "Total items before filtering:",
        this.flattenedMgpResults.length
      )
      console.log(
        "flattenedMgpResults sample:",
        this.flattenedMgpResults.slice(0, 5)
      )

      this.isInitialLoadMgp = false
      const activeFilters = Object.entries(this.columnFiltersMgp).filter(
        ([key, value]) => value !== ""
      )

      if (activeFilters.length === 0) {
        this.filteredResultsMgp = this.flattenedMgpResults
      } else {
        this.filteredResultsMgp = this.flattenedMgpResults.filter((item) => {
          return activeFilters.every(([key, filter]) => {
            console.log(
              `Filtering item ${JSON.stringify(
                item
              )} on key: ${key}, with filter: ${filter}`
            )

            if (!item.hasOwnProperty(key)) {
              console.warn(`Item does not have property ${key}`)
              return false
            }

            filter = filter.toLowerCase()
            const itemValue = item[key]
              ? item[key].toString().toLowerCase()
              : ""

            if (key === "flowDate") {
              const normalizedItemDate = this.normalizeDate(itemValue)
              const normalizedFilterDate = this.normalizeDate(filter)
              console.log(
                `Normalized dates - Item: ${normalizedItemDate}, Filter: ${normalizedFilterDate}`
              )

              if (filter.includes("/")) {
                const [filterDay, filterMonth] = filter.split("/")
                const itemDate = new Date(normalizedItemDate)
                return (
                  itemDate.getDate() === parseInt(filterDay) &&
                  itemDate.getMonth() + 1 === parseInt(filterMonth)
                )
              }

              return normalizedItemDate.includes(normalizedFilterDate)
            } else {
              const result = itemValue.includes(filter)
              console.log(`Comparing ${itemValue} with ${filter}: ${result}`)
              return result
            }
          })
        })
      }

      console.log(
        "Total items after filtering:",
        this.filteredResultsMgp.length
      )
      this.currentPage = 1
    },
    resetFiltersMgp () {
      // Resetta tutti i filtri
      Object.keys(this.columnFiltersMgp).forEach((key) => {
        this.columnFiltersMgp[key] = ""
      })
      // Ripristina i risultati originali
      this.filteredResultsMgp = this.flattenedMgpResults
      this.currentPage = 1
      this.isInitialLoadMgp = true
    },
    onFiltered (filteredItems) {
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },
    onFilteredMgp (filteredItems) {
      this.totalRowsMgp = filteredItems.length
      this.currentPage = 1
    },
    onFilteredMtf (elementsFiltered) {
      this.totalRowsMtf = elementsFiltered.length
      this.currentPage = 1
    },
    normalizeDate (dateString) {
      if (!dateString) return ""

      let date
      if (dateString.includes("/")) {
        // Formato dd/mm/yyyy o dd/mm/yyyy HH:MM o dd/mm o dd
        const parts = dateString.split(" ")[0].split("/")
        const currentDate = new Date()
        const currentYear = currentDate.getFullYear()
        const currentMonth = currentDate.getMonth()

        if (parts.length === 3) {
          // dd/mm/yyyy
          const [day, month, year] = parts
          date = new Date(year, month - 1, day)
        } else if (parts.length === 2) {
          // dd/mm
          const [day, month] = parts
          date = new Date(currentYear, month - 1, day)
        } else if (parts.length === 1) {
          // dd
          const [day] = parts
          date = new Date(currentYear, currentMonth, day)
        }
      } else {
        // Formato ISO dal database
        date = new Date(dateString)
      }

      if (isNaN(date.getTime())) return ""

      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, "0")
      const day = String(date.getDate()).padStart(2, "0")

      return `${year}-${month}-${day}`
    }
    /*   getErrorModalId (item) {
        return 'modal-errors-' + item.marketSession + '-' + item.pod
      } */
  },
  created () {
    // Inizializza filteredResults con tutti i risultati
    this.filteredResults = this.flattenedResults
  },
  watch: {
    flattenedResults: {
      immediate: true,
      handler (newValue) {
        if (this.isInitialLoad) {
          this.filteredResults = newValue
        }
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Settlement.vue